// ApiKeyPanel.tsx
import React, { useEffect, useState, useCallback } from "react";
import { Card, Title, Text, Group, Button, Stack, Divider, Code, Tooltip, Alert, Badge, Checkbox } from "@mantine/core";
import { MCLoader, FailedAlert } from "../../components";
import { useGetUserApikeyQuery, usePostUserApikeyMutation, useDeleteUserApikeyMutation } from "../../../openapi";
import { setShowFailedMessageThunk } from "../../global/globalSlice";
import { useAppDispatch } from "../../hooks";

const fmt = (d?: string | null) => (d ? new Date(d).toLocaleString() : "–");

type ConfirmAction = "revoke" | "rotate" | null;

const ApiKeyPanel: React.FC = () => {
  const { data: status, isLoading, isError } = useGetUserApikeyQuery();
  const [createKey, { isLoading: isCreating, isError: isCreateError }] = usePostUserApikeyMutation();
  const [revokeKey, { isLoading: isRevoking, isError: isRevokeError }] = useDeleteUserApikeyMutation();

  // Vises kun én gang etter opprettelse/rotasjon
  const [freshKey, setFreshKey] = useState<string | null>(null);

  // Inline-bekreftelse
  const [confirmAction, setConfirmAction] = useState<ConfirmAction>(null);
  const [confirmChecked, setConfirmChecked] = useState(false);

  const hasActive = Boolean(status && !status?.revokedAt);
  const hasError = isError || isCreateError || isRevokeError;

  // Hjelpere (uten sideeffekt)
  const dispatch = useAppDispatch(); // sørg for at useAppDispatch og setShowFailedMessageThunk er importert

  const generateKeyRaw = useCallback(async (): Promise<string> => {
    try {
      const res = await createKey({ body: {} } as any).unwrap();
      return res.apiKey as string;
    } catch (e: any) {
      dispatch(setShowFailedMessageThunk("Kunne ikke generere API-nøkkel"));
    }
  }, [createKey, dispatch]);

  // Primær når ingen nøkkel finnes
  const onGenerate = useCallback(async () => {
    const key = await generateKeyRaw();
    setFreshKey(key);
  }, [generateKeyRaw]);

  // Opphev
  const onRevoke = useCallback(async () => {
    try {
      await revokeKey().unwrap();
      setFreshKey(null);
    } catch (e) {
      dispatch(setShowFailedMessageThunk("Kunne ikke oppheve API-nøkkel"));
    }
  }, [revokeKey, dispatch]);

  // Auto-slett klartekst etter 60 sek
  useEffect(() => {
    if (!freshKey) return;
    const t = setTimeout(() => setFreshKey(null), 60_000);
    return () => clearTimeout(t);
  }, [freshKey]);

  if (isLoading) return <MCLoader />;
  if (hasError) return <FailedAlert />;

  const startConfirm = (action: Exclude<ConfirmAction, null>) => {
    setConfirmAction(action);
    setConfirmChecked(false);
  };
  const cancelConfirm = () => {
    setConfirmAction(null);
    setConfirmChecked(false);
  };
  const runConfirm = async () => {
    if (confirmAction === "revoke") {
      await onRevoke();
      cancelConfirm();
    } else if (confirmAction === "rotate") {
      // hent ny nøkkel, så vis etterpå
      const key = await generateKeyRaw();
      cancelConfirm();
      setFreshKey(key);
    }
  };

  const confirmBody =
    confirmAction === "revoke"
      ? "Eksisterende integrasjoner (f.eks. Power BI, skript) som bruker denne nøkkelen vil slutte å virke umiddelbart. Du kan generere en ny nøkkel senere og må da oppdatere alle integrasjoner."
      : "Nåværende nøkkel blir opphevet og en ny nøkkel genereres. Eksisterende integrasjoner må oppdateres til den nye nøkkelen. Den nye nøkkelen vises kun én gang—lagre den sikkert.";

  const confirmPrimaryLoading = confirmAction === "revoke" ? isRevoking : isCreating;

  return (
    <Card withBorder radius="lg" shadow="sm">
      <Stack gap="md">
        <Group justify="space-between" align="center">
          <Group gap="xs">
            <i className="fa fa-key fa-fw" />
            <Title order={5} m={0}>
              API-nøkkel
            </Title>
          </Group>

          {!hasActive ? (
            <Button
              variant="secondary"
              leftSection={<i className="fa fa-plus fa-fw" />}
              onClick={onGenerate}
              loading={isCreating || isLoading}
            >
              Generer ny nøkkel
            </Button>
          ) : (
            <Group>
              <Button
                variant="secondary"
                leftSection={<i className="fa fa-ban fa-fw" />}
                onClick={() => startConfirm("revoke")}
              >
                Opphev
              </Button>
              <Button
                variant="secondary"
                leftSection={<i className="fa fa-refresh fa-fw" />}
                onClick={() => startConfirm("rotate")}
              >
                Roter
              </Button>
            </Group>
          )}
        </Group>

        {/* Inline-bekreftelse */}
        {confirmAction && (
          <Alert color="red" variant="light" icon={<i className="fa fa-warning" />}>
            <Stack gap="sm">
              <Text size="sm">{confirmBody}</Text>
              <Checkbox
                checked={confirmChecked}
                onChange={(e) => setConfirmChecked(e.currentTarget.checked)}
                label="Jeg forstår at eksisterende integrasjoner må oppdateres"
              />
              <Group justify="flex-end">
                <Button variant="secondary" onClick={cancelConfirm}>
                  Avbryt
                </Button>
                <Button onClick={runConfirm} disabled={!confirmChecked} loading={confirmPrimaryLoading}>
                  {confirmAction === "revoke" ? "Opphev nøkkel" : "Roter nøkkel"}
                </Button>
              </Group>
            </Stack>
          </Alert>
        )}

        <Divider />

        {/* Vis klartekst-nykkel én gang */}
        {freshKey && (
          <Alert variant="light" color="yellow" icon={<i className="fa fa-exclamation-triangle" />}>
            <Stack gap="xs">
              <Text fw={600}>Viktig informasjon</Text>
              <Text size="sm">
                Dette er eneste gang denne API-nøkkelen vises. Nøkkelen er <b>personlig</b> og må <b>ikke deles</b>.
              </Text>
              <Group justify="space-between" wrap="nowrap" mt="xs">
                <Code style={{ overflowX: "auto" }}>{freshKey}</Code>
                <Tooltip label="Kopier nøkkel" withinPortal>
                  <Button
                    variant="light"
                    onClick={() => navigator.clipboard.writeText(freshKey)}
                    leftSection={<i className="fa fa-copy fa-fw" />}
                  >
                    Kopier
                  </Button>
                </Tooltip>
              </Group>
              <Text size="sm">
                Bruk som HTTP-header: <Code>Authorization: Bearer &lt;din-nøkkel&gt;</Code>
              </Text>
            </Stack>
          </Alert>
        )}

        {/* Status */}
        <Stack gap="xs">
          <Group justify="space-between">
            <Text size="sm" c="dimmed">
              Status
            </Text>
            {hasActive ? (
              <Badge color="green" variant="light">
                Aktiv
              </Badge>
            ) : (
              <Badge color="gray" variant="light">
                Ingen aktiv
              </Badge>
            )}
          </Group>

          <Group justify="space-between">
            <Text size="sm" c="dimmed">
              Prefix
            </Text>
            <Code>{status?.prefix ?? "–"}</Code>
          </Group>

          <Group justify="space-between">
            <Text size="sm" c="dimmed">
              Sist brukt
            </Text>
            <Text>{fmt(status?.lastUsedAt)}</Text>
          </Group>

          <Group justify="space-between">
            <Text size="sm" c="dimmed">
              Brukstell
            </Text>
            <Text>{status?.useCount ?? 0}</Text>
          </Group>

          {status?.revokedAt && (
            <Group justify="space-between">
              <Text size="sm" c="dimmed">
                Opphevet
              </Text>
              <Text>{fmt(status.revokedAt)}</Text>
            </Group>
          )}
        </Stack>

        {!freshKey && !hasActive && (
          <Alert variant="light">
            <Text size="sm">
              Ingen aktiv nøkkel. Klikk{" "}
              <Text span fw={600}>
                Generer ny nøkkel
              </Text>{" "}
              for å opprette en.
            </Text>
          </Alert>
        )}
      </Stack>
    </Card>
  );
};

export default ApiKeyPanel;
